import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, ChevronDown, ChevronLeft, MapPin, Search } from 'lucide-react';
import { Calendar } from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import apiService from '../services/apiService';

interface SchedulingAndLocationProps {
  onNextStep: (details: {
    date: string;
    time: string;
    quartier: string;
  }) => void;
  onBackStep: () => void;
}

export default function SchedulingAndLocation({ onNextStep, onBackStep }: SchedulingAndLocationProps) {
  const { t } = useTranslation();
  const [date, setDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [time, setTime] = useState('');
  const [quartier, setQuartier] = useState('');
  const [showQuartierList, setShowQuartierList] = useState(false);
  const [showTimeList, setShowTimeList] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [quartiers, setQuartiers] = useState<string[]>([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [error, setError] = useState('');

  // Fetch available locations on component mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await apiService.getLocations();
        if (response.success) {
          setQuartiers(response.data.locations);
        } else {
          setError(t('scheduling.errorLocations'));
        }
      } catch (err) {
        console.error('Error fetching locations:', err);
        setError(t('scheduling.errorLocations'));
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [t]);

  // Fetch available time slots when date changes
  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!date) return;
      
      try {
        setLoadingTimeSlots(true);
        setTime(''); // Reset selected time when date changes
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        const response = await apiService.getTimeSlots(formattedDate);
        
        if (response.success) {
          setAvailableTimeSlots(response.data.available_slots);
          
          // Show message if no slots available
          if (response.data.available_slots.length === 0 && response.data.message) {
            setError(response.data.message);
          } else {
            setError('');
          }
        } else {
          setError(t('scheduling.errorTimeSlots'));
        }
      } catch (err) {
        console.error('Error fetching time slots:', err);
        setError(t('scheduling.errorTimeSlots'));
      } finally {
        setLoadingTimeSlots(false);
      }
    };

    if (date) {
      fetchTimeSlots();
    }
  }, [date, t]);

  const handleDateChange = (newDate: Date) => {
    setDate(newDate);
    setShowCalendar(false);
  };

  const filteredQuartiers = quartiers.filter(q => 
    q.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleTimeSelection = (selectedTime: string) => {
    setTime(selectedTime);
    setShowTimeList(false);
  };

  const handleQuartierSelection = (selectedQuartier: string) => {
    setQuartier(selectedQuartier);
    setShowQuartierList(false);
    setSearchTerm('');
  };

  const handleSubmit = () => {
    if (date && time && quartier) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      onNextStep({
        date:formattedDate,
        time,
        quartier
      });
    }
  };

  if (loadingLocations && !quartiers.length) {
    return (
      <div className="flex flex-col min-h-screen bg-[#ededed] items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-[#2196F3] mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !date) {
    return (
      <div className="flex flex-col min-h-screen bg-[#ededed] items-center justify-center">
        <div className="text-center p-6 bg-white rounded-lg shadow-md max-w-sm">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold mb-2">{t('scheduling.errorTitle')}</h3>
          <p className="mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#2196F3] text-white rounded-full shadow-md"
          >
            {t('scheduling.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#ededed]">
      <div className="flex flex-col items-center px-4 max-w-md mx-auto w-full">
        <button
          onClick={onBackStep}
          className="self-start relative top-12 mb-4 flex items-center text-blue-500"
          aria-label={t('common.backButton')}
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          {t('common.back')}
        </button>
        <h1 className="text-[32px] text-center mb-4 font-light">
          {t('scheduling.title')}
        </h1>

        <p className="text-center text-[16px] mb-8 font-light">
          {t('scheduling.subtitle')}
        </p>

        <div className="w-full space-y-6">
          {/* Date Selection */}
          <div className="space-y-2">
            <div className="relative">
              <input
                type="text"
                value={date ? date.toLocaleDateString() : ''}
                readOnly
                placeholder={t('scheduling.datePlaceholder')}
                className="w-full h-12 pl-4 pr-12 border border-gray-300 rounded-lg bg-white"
                onClick={() => setShowCalendar(!showCalendar)}
              />
              <button 
                className="absolute right-4 top-1/2 -translate-y-1/2"
                onClick={() => setShowCalendar(!showCalendar)}
                aria-label={t('scheduling.toggleCalendar')}
              >
                <CalendarIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            {showCalendar && (
              <div className="absolute z-10 bg-white shadow-lg rounded-lg">
                <Calendar 
                  minDate={new Date()} 
                  locale='fr' 
                  //@ts-ignore
                  onChange={handleDateChange} 
                  value={date}
                  tileDisabled={({ date, view }) => 
                    view === 'month' && date.getDay() === 0 // Disable Sundays
                  }
                />
              </div>
            )}
            <p className="text-sm text-gray-600 font-light">
              {t('scheduling.dateAvailability')}
            </p>
          </div>

          {/* Time Selection */}
          <div className="relative">
            <button 
              className={`w-full h-12 px-4 border border-gray-300 rounded-lg text-left flex items-center justify-between bg-white ${
                !date || loadingTimeSlots ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={() => date && !loadingTimeSlots && setShowTimeList(!showTimeList)}
              disabled={!date || loadingTimeSlots}
            >
              {loadingTimeSlots ? (
                <div className="flex items-center">
                  <svg className="animate-spin h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-gray-400">{t('common.loading')}</span>
                </div>
              ) : (
                <span className={time ? 'text-black' : 'text-gray-400'}>
                  {time || t('scheduling.timePlaceholder')}
                </span>
              )}
              <ChevronDown className="w-5 h-5 text-gray-400" />
            </button>
            {showTimeList && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {availableTimeSlots.length === 0 ? (
                  <div className="p-3 text-center text-gray-500">
                    {error || t('scheduling.noTimeSlotsAvailable')}
                  </div>
                ) : (
                  availableTimeSlots.map((slot) => (
                    <button
                      key={slot}
                      className="w-full p-2 text-left hover:bg-gray-100"
                      onClick={() => handleTimeSelection(slot)}
                    >
                      {slot}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Quartier Selection with Search */}
          <div className="relative">
            <button 
              className="w-full h-12 px-4 border border-gray-300 rounded-lg text-left flex items-center justify-between bg-white"
              onClick={() => setShowQuartierList(!showQuartierList)}
            >
              <span className={quartier ? 'text-black' : 'text-gray-400'}>
                {quartier || t('scheduling.quartierPlaceholder')}
              </span>
              <ChevronDown className="w-5 h-5 text-gray-400" />
            </button>
            {showQuartierList && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                <div className="sticky top-0 bg-white p-2 border-b">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t('scheduling.searchQuartier')}
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-md"
                    />
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  </div>
                </div>
                {filteredQuartiers.map((quar) => (
                  <button
                    key={quar}
                    className="w-full p-2 text-left hover:bg-gray-100"
                    onClick={() => handleQuartierSelection(quar)}
                  >
                    {quar}
                  </button>
                ))}
              </div>
            )}
          </div>

          {error && date && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}
        </div>
        
        {/* Submit Button */}
        <button 
          className={`w-[90%] py-4 px-6 mt-5 border-[#2196F3] border-2 font-bold rounded-full bg-white text-[#2196F3] shadow-lg text-lg transition-colors ${
            !date || !time || !quartier ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
          }`}
          onClick={handleSubmit}
          disabled={!date || !time || !quartier}
        >
          {t('scheduling.proceedToPayment')}
        </button>
      </div>
    </div>
  );
}